import { format } from "date-fns";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { theme } from "src/config";
import { Item } from "src/models";
import { ItemStores } from "src/stores";
import { IItem, IItemLink, ItemType } from "src/types";
import styled from "styled-components";
import { Icon } from ".";
import { IMultiSelectProps, ItemRendererProps, MultiSelect, Option } from "./multiSelect";
import { Text } from "./text";


/**
 * Generic multi select component, styled to follow the OP theme
 * 
 * @param options       Option[]  Options to choose from
 * @param selected      Option[]  Pre-selected values
 * @param singleSelect  ?boolean  User can only select one item
 * @param forceSelect   ?boolean  Force at least one selection
 * @param disabled      ?boolean  Disable input
 * @param placeholder   ?string   String shown in empty input field 
 * @param onChange      ?(selection: Option | null): void Called when single Select is active
 * @param onAdd         ?(selection: Option): void        Called in a multiselect when an item was added
 * @param onRemove      ?(selection: Option): void        Called in a multiselect when an item was removed
 * @param onOpenSelect  ?(): void                         Called when the select menu is opened
 */
export const ItemSelect = inject("itemStores")(observer((props: IMultiSelectProps & {itemStores?: ItemStores, itemType: ItemType}): JSX.Element => {
  const containerRef = React.useRef<HTMLDivElement>();
  const headerRef    = React.useRef<HTMLDivElement>();
  const [width, setWidth] = React.useState<number>(0); // TODO use this to dertermine to add a <...> placeholder if there are too many mini avatars for the available space
  const { t, i18n } = useTranslation();
  const { 
    disabled, 
    selected, 
    singleSelect, 
    onLoad,
    showLoadMore,
    itemStores,
    itemType,
  } = props;

  React.useEffect(() => {
    const headerEls: HTMLCollectionOf<HTMLDivElement> = containerRef?.current?.getElementsByClassName("dropdown-heading-value") as HTMLCollectionOf<HTMLDivElement>;
    headerRef.current = headerEls.item(0);
  }, [containerRef]);
  React.useEffect(() => {
    setWidth(headerRef?.current?.clientWidth);
  }, [headerRef?.current?.clientWidth]);

  const loadMoreOptionKey = "#load_more";

  const itemRenderer = ({ checked, option, disabled, onClick }: ItemRendererProps): React.ReactNode => {
    const item: Item<IItem> = itemStores[itemType].linkOptions.getItems[option.value];
    if(!item) return null;
    return (
      <OptionItemContainer 
        className = {`item-renderer ${disabled ? "disabled" : ""}`}
      >
        <input
          type="checkbox"
          onChange={option.key === loadMoreOptionKey ? onLoad : onClick}
          checked={checked}
          tabIndex={-1}
          disabled={disabled}
          style={{ height: 0, width: 0, visibility: 'hidden', position: 'absolute' }}
        />
        {option.key === loadMoreOptionKey ?
          <span>
            {item}
          </span> 
        :
          <>
            <span>{option.label}</span>
            <span>{`(#${item.number} ${item.date && format(new Date(item.date), "yyyy-MM-dd")})`}</span>
          </>
        }
      </OptionItemContainer>
    );
  }

  // TODO Change the "v." in the label based on the current language
  const valueRenderer = (selected: Option[]) => {
    const items: IItemLink[] = selected.map(s => itemStores[itemType].links.getItems[s.value]).filter(i => Boolean(i));
    if (items.length) {
      return (
        <ValueContainer>
          {items.map((i: IItemLink) => (
            <Chip key={i.id}>
              <Text size="xsmall" color="white" as="span">
                {`#${i.number} (v.${i.date && format(i.date, "w, yyyy")})`} 
              </Text>
              <Icon cursor="pointer" style={{marginLeft: "0.5rem"}} onClick={(e: React.MouseEvent<SVGElement, MouseEvent>) => {
                  props.onRemove(selected.find(o => o.value === i.id));
                  e.preventDefault();
                  e.stopPropagation();
                }} 
              size="xsmall" iconId="close"/>
            </Chip>
          ))}
        </ValueContainer>
      );
    } else return (
      <ValueContainer>
        <Text size="xsmall" color={theme.color.disabled} as="span">{""}</Text>
      </ValueContainer>
    );
  }

  return (
    <Container ref={containerRef} disabled={disabled}>
      {disabled ?
      <DisabledContainer>
        <span>{props.label}</span>
        {(props.valueRenderer || valueRenderer)(selected)}
      </DisabledContainer>
      : <ItemMultiSelect 
          {...props} 
          itemRenderer  = {props.itemRenderer  || itemRenderer}
          valueRenderer = {props.valueRenderer || valueRenderer}
        />
      }
    </Container>
  );
}));


const DisabledContainer = styled.div`
  display:     flex;
  flex-direction: column;
  span {
    font-size:   ${theme.font.size.xxsmall};
  }
`;


const ItemMultiSelect = styled(MultiSelect)`
`;

const OptionItemContainer = styled.div`
  display:         flex;
  justify-content: space-between;
`;

const Chip = styled.div`
  display:          flex;
  align-items:      center;
  justify-content:  center;
  background-color: ${theme.color.primary7};
  min-height:       1.5rem;
  padding:          0.25rem 0.25rem;
  border-radius:    0.3rem;
`;

const ValueContainer = styled.div`
  display:     flex;
  align-items: center;
  flex-wrap:   wrap;
  gap:         0.25rem;
  z-index:     2;
  margin:      0.25rem 0.35rem;
  align-items: center;
`;

const Container = styled.div<{disabled?: boolean}>`
  display:       ${props => props.disabled ? "flex" : undefined};
  border-bottom: ${props => props.disabled ? `1px solid ${theme.color.disabled}`: undefined};
`;
